import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  GithubAuthProvider,
  signInWithEmailAndPassword as signInWithEmailAndPasswordFirebase,
  signOut as signOutFirebase,
  onAuthStateChanged,
  FacebookAuthProvider,
  PhoneAuthProvider,
  signInWithCredential,
  browserLocalPersistence,
  setPersistence,
  signInWithCustomToken,
  Auth
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  updateDoc 
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import type { AuthUser, User } from '../types/auth';
import { passkeyService } from '../services/passkey';
import { userService } from '../services/user';
import { exchangeConnectorService } from '../services/exchangeConnector';
import * as OTPAuth from 'otpauth';
import deviceWebCryptoService from '../services/deviceWebCrypto';

// Constants for storage keys
const TOKEN_STORAGE_KEY = 'token';
const SESSION_ID_STORAGE_KEY = 'sessionId';
const USER_STORAGE_KEY = 'user';
const FIREBASE_PERSISTENCE_KEY = 'firebase_persistence_set';

// Debug function to log all localStorage items
const debugLocalStorage = () => {
  console.log('==== DEBUG: localStorage contents ====');
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      try {
        const value = localStorage.getItem(key);
        console.log(`${key}: ${value?.substring(0, 50)}${value && value.length > 50 ? '...' : ''}`);
      } catch (e) {
        console.log(`${key}: [Error reading value]`);
      }
    }
  }
  console.log('====================================');
};

// Declare global window properties for authentication
declare global {
  interface Window {
    recaptchaVerifier: any;
    verificationId: string;
    tokenRefreshInterval: NodeJS.Timeout | null;
  }
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SIGN_OUT' };

// Initialize state with user from localStorage if available
const getUserFromStorage = (): User | null => {
  try {
    console.log('DEBUG: Attempting to get user from localStorage');
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);
    console.log(`DEBUG: storedUser exists: ${!!storedUser}`);
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      console.log(`DEBUG: Parsed user ID: ${parsedUser?.id}, Email: ${parsedUser?.email}`);
      return parsedUser;
    }
  } catch (error) {
    console.error('Error parsing stored user:', error);
  }
  return null;
};

const initialState: AuthState = {
  user: getUserFromStorage(),
  loading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_USER':
      // Persist user to localStorage when it's set
      if (action.payload) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(action.payload));
      } else {
        localStorage.removeItem(USER_STORAGE_KEY);
        // Also remove token when user is removed
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(SESSION_ID_STORAGE_KEY);
      }
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };
    case 'SIGN_OUT':
      // Clear user from localStorage on sign out
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(SESSION_ID_STORAGE_KEY);
      return {
        ...state,
        user: null,
        error: null,
      };
    default:
      return state;
  }
};

interface AuthContextType {
  state: AuthState;
  dispatch: React.Dispatch<AuthAction>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithPasskey: () => Promise<void>;
  signInWithDeviceWebCrypto: (email?: string) => Promise<void>;
  sendSmsCode: (phoneNumber: string) => Promise<void>;
  verifySmsCode: (code: string) => Promise<void>;
  verifyTOTP: (email: string, code: string) => Promise<void>;
  updateUser: (userId: string, updates: Partial<User>) => Promise<void>;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType>({
  state: initialState,
  dispatch: () => {},
  signOut: async () => {},
  signInWithGoogle: async () => {},
  signInWithFacebook: async () => {},
  signInWithPasskey: async () => {},
  signInWithDeviceWebCrypto: async () => {},
  sendSmsCode: async () => {},
  verifySmsCode: async () => {},
  verifyTOTP: async () => {},
  updateUser: async () => {},
  signInWithEmailAndPassword: async () => null,
});

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize exchange connections when user is set
  useEffect(() => {
    const initializeExchangeConnections = async () => {
      if (state.user?.id) {
        console.log('User authenticated, initializing exchange connections...');
        try {
          const connectionResults = await exchangeConnectorService.initializeAllConnections(state.user.id);
          console.log('Exchange connection results:', connectionResults);
        } catch (error) {
          console.error('Failed to initialize exchange connections:', error);
        }
      }
    };

    initializeExchangeConnections();
  }, [state.user?.id]);

  // Create a session with the backend
  const createSession = async (token: string): Promise<string | null> => {
    try {
      console.log('DEBUG: Creating backend session with token');
      const apiUrl = `${process.env.REACT_APP_API_URL}/auth/session`;
      console.log(`DEBUG: API URL for session creation: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`DEBUG: Session creation response status: ${response.status}`);
      
      if (!response.ok) {
        console.error(`DEBUG: Failed to create session: ${response.status}`);
        const errorText = await response.text();
        console.error(`DEBUG: Error response: ${errorText}`);
        throw new Error(`Failed to create session: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('DEBUG: Session creation response:', data);
      
      if (data.success && data.sessionId) {
        console.log(`DEBUG: Session created successfully: ${data.sessionId}`);
        localStorage.setItem(SESSION_ID_STORAGE_KEY, data.sessionId);
        debugLocalStorage();
        return data.sessionId;
      } else {
        console.error('DEBUG: Failed to create session:', data.message);
        return null;
      }
    } catch (error) {
      console.error('DEBUG: Error creating session:', error);
      return null;
    }
  };

  // Validate a session with the backend
  const validateSession = async (sessionId: string): Promise<boolean> => {
    try {
      console.log(`DEBUG: Validating session: ${sessionId}`);
      
      // Special handling for passkey-generated session IDs
      if (sessionId.startsWith('passkey-')) {
        console.log('DEBUG: Detected passkey-generated session ID, validating locally');
        
        // Extract user ID from the passkey session ID format: passkey-{email}-{timestamp}
        const parts = sessionId.split('-');
        if (parts.length >= 3) {
          const email = parts[1];
          
          // Check if the user exists in Firestore
          try {
            const user = await userService.getUserProfile(email);
            if (user) {
              console.log(`DEBUG: Passkey session validated successfully for user: ${email}`);
              return true;
            }
          } catch (error) {
            console.error('DEBUG: Error validating passkey user:', error);
          }
        }
        
        console.log('DEBUG: Failed to validate passkey-generated session');
        return false;
      }
      
      // Regular session validation with backend
      const apiUrl = `${process.env.REACT_APP_API_URL}/auth/session/${sessionId}`;
      console.log(`DEBUG: API URL for session validation: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`DEBUG: Session validation response status: ${response.status}`);
      
      if (!response.ok) {
        console.warn(`DEBUG: Session validation failed with status: ${response.status}`);
        const errorText = await response.text();
        console.warn(`DEBUG: Error response: ${errorText}`);
        return false;
      }
      
      const data = await response.json();
      console.log('DEBUG: Session validation response:', data);
      
      if (data.valid === true) {
        console.log(`DEBUG: Session validated successfully with userId: ${data.userId}`);
        return true;
      }
      console.log('DEBUG: Session validation returned invalid session');
      return false;
    } catch (error) {
      console.error('DEBUG: Error validating session:', error);
      return false;
    }
  };

  // Refresh a session with a new token
  const refreshSession = async (sessionId: string, token: string): Promise<boolean> => {
    try {
      console.log('DEBUG: Refreshing session:', sessionId);
      
      // Special handling for passkey-generated session IDs
      if (sessionId.startsWith('passkey-')) {
        console.log('DEBUG: Detected passkey-generated session ID, no refresh needed');
        // Passkey sessions don't need to be refreshed as they're validated locally
        return true;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/session/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`DEBUG: Session refresh response status: ${response.status}`);
      
      if (!response.ok) {
        console.warn(`DEBUG: Session refresh failed with status: ${response.status}`);
        return false;
      }
      
      const data = await response.json();
      console.log('DEBUG: Session refresh response:', data);
      
      return data.success === true;
    } catch (error) {
      console.error('DEBUG: Error refreshing session:', error);
      return false;
    }
  };

  // Invalidate a session with the backend
  const invalidateSession = async (sessionId: string): Promise<boolean> => {
    try {
      console.log('DEBUG: Invalidating session:', sessionId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/session/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`DEBUG: Session invalidation response status: ${response.status}`);
      
      if (!response.ok) {
        console.warn(`DEBUG: Session invalidation failed with status: ${response.status}`);
        const errorText = await response.text();
        console.warn(`DEBUG: Error response: ${errorText}`);
        return false;
      }
      
      const data = await response.json();
      console.log('DEBUG: Session invalidation response:', data);
      
      return data.success === true;
    } catch (error) {
      console.error('DEBUG: Error invalidating session:', error);
      return false;
    }
  };

  // Set up Firebase persistence
  const setupFirebasePersistence = async () => {
    try {
      // Check if we've already set persistence in this browser session
      if (sessionStorage.getItem(FIREBASE_PERSISTENCE_KEY) !== 'true') {
        console.log('DEBUG: Setting up Firebase Auth persistence...');
        await setPersistence(auth, browserLocalPersistence);
        sessionStorage.setItem(FIREBASE_PERSISTENCE_KEY, 'true');
        console.log('DEBUG: Firebase Auth persistence set to local');
      }
    } catch (error) {
      console.error('DEBUG: Error setting Firebase persistence:', error);
    }
  };

  // Set up token refresh interval
  const setupTokenRefreshInterval = () => {
    // Clear any existing interval
    if (window.tokenRefreshInterval) {
      clearInterval(window.tokenRefreshInterval);
    }
    
    // Set up a new interval to refresh the token every 5 minutes
    window.tokenRefreshInterval = setInterval(async () => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const refreshedToken = await currentUser.getIdToken(true);
          localStorage.setItem(TOKEN_STORAGE_KEY, refreshedToken);
          console.log('DEBUG: Firebase token refreshed via interval');
          
          // Also refresh the session with the backend
          const sessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
          if (sessionId) {
            const refreshed = await refreshSession(sessionId, refreshedToken);
            if (refreshed) {
              console.log('DEBUG: Session refreshed successfully');
            } else {
              console.warn('DEBUG: Failed to refresh session, will try again later');
            }
          }
        } else {
          if (window.tokenRefreshInterval) {
            clearInterval(window.tokenRefreshInterval);
            window.tokenRefreshInterval = null;
          }
        }
      } catch (refreshError) {
        console.error('DEBUG: Error refreshing token:', refreshError);
      }
    }, 5 * 60 * 1000); // Refresh token every 5 minutes
  };

  useEffect(() => {
    console.log('DEBUG: Setting up Firebase auth state listener and restoring session...');
    debugLocalStorage();
    
    // Set loading state while we check authentication
    dispatch({ type: 'SET_LOADING', payload: true });
    
    // First set up Firebase persistence
    setupFirebasePersistence();
    
    const restoreSession = async () => {
      try {
        // First try to restore from localStorage to prevent flashing
        const storedUser = getUserFromStorage();
        const sessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
        
        console.log('DEBUG: Checking for stored session:', { 
          hasStoredUser: !!storedUser, 
          sessionId: sessionId,
          userId: storedUser?.id
        });
        
        if (storedUser && sessionId) {
          console.log('DEBUG: Found stored user and session data, validating session');
          
          // Check if the session is still valid with the backend
          const isValid = await validateSession(sessionId);
          
          if (isValid) {
            console.log('DEBUG: Session is valid, maintaining user session');
            dispatch({ type: 'SET_USER', payload: storedUser });
            
            // Get a fresh token from Firebase if possible
            if (auth.currentUser) {
              const token = await auth.currentUser.getIdToken(true);
              console.log('DEBUG: Got fresh token from Firebase');
              localStorage.setItem(TOKEN_STORAGE_KEY, token);
              setupTokenRefreshInterval();
            } else {
              console.log('DEBUG: No Firebase current user available');
            }
            
            dispatch({ type: 'SET_LOADING', payload: false });
            return true;
          } else {
            console.log('DEBUG: Session is invalid, clearing stored data');
            localStorage.removeItem(SESSION_ID_STORAGE_KEY);
            localStorage.removeItem(USER_STORAGE_KEY);
            localStorage.removeItem(TOKEN_STORAGE_KEY);
          }
        } else {
          console.log('DEBUG: No stored user or session ID found');
        }
        
        return false;
      } catch (error) {
        console.error('DEBUG: Error restoring session:', error);
        return false;
      }
    };
    
    // Try to restore the session first
    restoreSession().then(sessionRestored => {
      // Set up the auth state listener regardless of session restoration result
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          if (firebaseUser) {
            console.log('DEBUG: Firebase auth state changed: User is signed in', firebaseUser.uid);
            
            try {
              // Get the Firebase ID token
              const idToken = await firebaseUser.getIdToken(true);
              localStorage.setItem(TOKEN_STORAGE_KEY, idToken);
              
              // Fetch the user profile
              const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
              let userData: User = {
                id: firebaseUser.uid,
                email: firebaseUser.email || '',
                displayName: firebaseUser.displayName || '',
                photoURL: firebaseUser.photoURL || '',
                provider: 'google', // Default provider, will be overwritten if exists in userDoc
                preferences: {
                  theme: 'system',
                  notifications: {
                    sms: false,
                    push: false,
                    trades: false,
                    performance: false
                  }
                },
                authMethods: {},
                createdAt: new Date().toISOString(),
                lastLoginAt: new Date().toISOString()
              };
              
              if (userDoc.exists()) {
                const userDocData = userDoc.data();
                userData = {
                  ...userData,
                  ...userDocData as Partial<User>,
                };
              }
              
              // Store user in localStorage for persistence
              localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
              
              // Create or refresh session with the backend
              const existingSessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
              if (existingSessionId) {
                // Try to refresh the existing session
                const refreshed = await refreshSession(existingSessionId, idToken);
                if (!refreshed) {
                  // If refresh fails, create a new session
                  console.log('DEBUG: Session refresh failed, creating new session');
                  await createSession(idToken);
                }
              } else {
                // Create a new session
                await createSession(idToken);
              }
              
              // Set up token refresh interval
              setupTokenRefreshInterval();
              
              // Update the user state
              dispatch({ type: 'SET_USER', payload: userData });
            } catch (error) {
              console.error('DEBUG: Error processing authenticated user:', error);
              dispatch({ type: 'SET_ERROR', payload: 'Failed to process authenticated user' });
            }
          } else {
            console.log('DEBUG: Firebase auth state changed: User is signed out');
            
            // Check if we have a valid session even though Firebase says user is signed out
            const storedUser = getUserFromStorage();
            const sessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
            
            if (storedUser && sessionId) {
              // Validate the session with the backend
              const isValid = await validateSession(sessionId);
              
              if (isValid) {
                console.log('DEBUG: Session is still valid despite Firebase signout, attempting to restore user state');
                dispatch({ type: 'SET_USER', payload: storedUser });
                setupTokenRefreshInterval();
                return;
              }
            }
            
            // If we get here, the user is truly signed out or session is invalid
            dispatch({ type: 'SIGN_OUT' });
            
            // Clear any stored data
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            localStorage.removeItem(USER_STORAGE_KEY);
            localStorage.removeItem(SESSION_ID_STORAGE_KEY);
          }
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      });
      
      // Clean up the auth state listener when the component unmounts
      return () => {
        unsubscribe();
        if (window.tokenRefreshInterval) {
          clearInterval(window.tokenRefreshInterval);
          window.tokenRefreshInterval = null;
        }
      };
    });
  }, [dispatch]);

  // Helper function to capture device information after successful authentication
  const captureDeviceInfo = async (userId: string, authMethod: string) => {
    try {
      // Update device info with the current user ID
      const deviceInfo = deviceWebCryptoService.updateDeviceInfoForUser(userId);
      
      // Add authentication method to device info
      deviceInfo.authMethod = authMethod;
      deviceInfo.lastSignIn = new Date().toISOString();
      
      console.log(`DEBUG: Captured device information for user ${userId} using ${authMethod}`, deviceInfo);
      
      // In the future, we could send this information to the backend for analysis
      // This would be useful for security monitoring and fraud detection
    } catch (error) {
      console.error('Failed to capture device information:', error);
      // Non-critical error, don't throw
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      // Get the session ID before signing out
      const sessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
      console.log(`DEBUG: Signing out, session ID: ${sessionId}`);
      
      // Invalidate the session with the backend if we have a session ID
      if (sessionId) {
        const invalidated = await invalidateSession(sessionId);
        console.log(`DEBUG: Session invalidation result: ${invalidated}`);
      }
      
      // Clear any token refresh interval
      if (window.tokenRefreshInterval) {
        clearInterval(window.tokenRefreshInterval);
        window.tokenRefreshInterval = null;
        console.log('DEBUG: Cleared token refresh interval');
      }
      
      // Sign out from Firebase
      await signOutFirebase(auth);
      console.log('DEBUG: Signed out from Firebase');
      
      // Clear local storage
      localStorage.removeItem(SESSION_ID_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      console.log('DEBUG: Cleared localStorage items');
      debugLocalStorage();
      
      // Clear user state
      dispatch({ type: 'SIGN_OUT' });
      console.log('DEBUG: Dispatched SIGN_OUT action');
    } catch (error) {
      console.error('DEBUG: Error signing out:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const provider = new GoogleAuthProvider();
      
      // Request additional permissions
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      
      if (result.user) {
        // Get and store the token
        const idToken = await result.user.getIdToken();
        localStorage.setItem(TOKEN_STORAGE_KEY, idToken);
        
        // Create a session on the backend
        console.log('DEBUG: Creating backend session after Google authentication');
        const sessionId = await createSession(idToken);
        if (sessionId) {
          console.log(`DEBUG: Session created successfully after Google auth: ${sessionId}`);
          debugLocalStorage();
        } else {
          console.error('DEBUG: Failed to create session after Google authentication');
        }
        
        if (result.user.email) {
          const user = await userService.getUserProfile(result.user.email);
          if (user) {
            // Store user in localStorage for persistence
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
            dispatch({ type: 'SET_USER', payload: user });
            
            // Set up token refresh interval
            setupTokenRefreshInterval();
            
            // Capture device information
            await captureDeviceInfo(user.id, 'google');
            
            // Debug localStorage contents
            debugLocalStorage();
            return;
          }
        }
      }
    } catch (error) {
      console.error('DEBUG: Error signing in with Google:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to sign in with Google',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signInWithFacebook = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const provider = new FacebookAuthProvider();
      
      // Request additional permissions
      provider.addScope('email');
      provider.addScope('public_profile');
      
      const result = await signInWithPopup(auth, provider);
      
      if (result.user) {
        // Get and store the token
        const idToken = await result.user.getIdToken();
        localStorage.setItem(TOKEN_STORAGE_KEY, idToken);
        
        // Create a session on the backend
        console.log('DEBUG: Creating backend session after Facebook authentication');
        const sessionId = await createSession(idToken);
        if (sessionId) {
          console.log(`DEBUG: Session created successfully after Facebook auth: ${sessionId}`);
          debugLocalStorage();
        } else {
          console.error('DEBUG: Failed to create session after Facebook authentication');
        }
        
        if (result.user.email) {
          const user = await userService.getUserProfile(result.user.email);
          if (user) {
            // Store user in localStorage for persistence
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
            dispatch({ type: 'SET_USER', payload: user });
            
            // Set up token refresh interval
            setupTokenRefreshInterval();
            
            // Capture device information
            await captureDeviceInfo(user.id, 'facebook');
            
            // Debug localStorage contents
            debugLocalStorage();
            return;
          }
        }
      }
    } catch (error) {
      console.error('DEBUG: Error signing in with Facebook:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to sign in with Facebook',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signInWithPasskey = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await passkeyService.authenticate();
      
      if (result.success && result.email) {
        console.log('DEBUG: Passkey authentication successful for email:', result.email);
        
        // Get the user from Firestore directly
        const user = await userService.getUserProfile(result.email);
        if (user) {
          // Store user in localStorage for persistence
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
          
          // Create a session with the backend using the dedicated passkey endpoint
          console.log('DEBUG: Creating backend session after passkey authentication');
          
          try {
            // Make a request to create a passkey session
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/passkey/session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                userId: result.email,
                email: result.email,
                timestamp: new Date().toISOString()
              })
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.sessionId) {
                // Store the session ID in localStorage
                localStorage.setItem(SESSION_ID_STORAGE_KEY, data.sessionId);
                console.log(`DEBUG: Session created successfully after passkey auth: ${data.sessionId}`);
                
                // Set the user in state
                dispatch({ type: 'SET_USER', payload: user });
                
                // Set up token refresh interval for session maintenance
                setupTokenRefreshInterval();
                
                // Capture device information
                await captureDeviceInfo(user.id, 'passkey');
                
                // Debug localStorage contents
                debugLocalStorage();
                return;
              }
            } else {
              console.error('DEBUG: Failed to create passkey session with backend:', await response.text());
            }
          } catch (error) {
            console.error('DEBUG: Error creating passkey session:', error);
          }
          
          // If the backend session creation fails, create a fallback session
          console.warn('DEBUG: Using fallback session creation for passkey authentication');
          const fallbackSessionId = `passkey-${result.email}-${Date.now()}`;
          localStorage.setItem(SESSION_ID_STORAGE_KEY, fallbackSessionId);
          console.log(`DEBUG: Created fallback session ID: ${fallbackSessionId}`);
          
          // Set the user in state
          dispatch({ type: 'SET_USER', payload: user });
          
          // Capture device information
          await captureDeviceInfo(user.id, 'passkey-fallback');
          
          // Debug localStorage contents
          debugLocalStorage();
          return;
        }
      }
      
      dispatch({ type: 'SET_ERROR', payload: 'Passkey authentication failed' });
    } catch (error) {
      console.error('DEBUG: Error in passkey sign in:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'An error occurred during passkey sign in',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signInWithDeviceWebCrypto = async (email?: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // If email is not provided, prompt the user for it
      const userEmail = email || prompt('Please enter your email address');
      if (!userEmail) {
        dispatch({ type: 'SET_ERROR', payload: 'Email is required for device authentication' });
        return;
      }
      
      // Authenticate with the device
      const result = await deviceWebCryptoService.authenticate(userEmail);
      
      if (result.success) {
        console.log('DEBUG: Device web crypto authentication successful for email:', result.email);
        
        // Get the user from Firestore directly
        const user = await userService.getUserProfile(result.email);
        if (user) {
          // Store user in localStorage for persistence
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
          
          // Create a session with the backend using the dedicated device endpoint
          console.log('DEBUG: Creating backend session after device authentication');
          
          try {
            // Make a request to create a device session
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/device/session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                userId: result.email,
                email: result.email,
                deviceId: result.deviceId,
                signature: result.signature,
                deviceInfo: result.deviceInfo
              })
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.sessionId) {
                // Store the session ID in localStorage
                localStorage.setItem(SESSION_ID_STORAGE_KEY, data.sessionId);
                console.log(`DEBUG: Session created successfully after device auth: ${data.sessionId}`);
                
                // Set the user in state
                dispatch({ type: 'SET_USER', payload: user });
                
                // Set up token refresh interval for session maintenance
                setupTokenRefreshInterval();
                
                // Capture device information
                await captureDeviceInfo(user.id, 'device');
                
                // Debug localStorage contents
                debugLocalStorage();
                return;
              }
            } else {
              console.error('DEBUG: Failed to create device session with backend:', await response.text());
            }
          } catch (error) {
            console.error('DEBUG: Error creating device session:', error);
          }
          
          // If the backend session creation fails, create a fallback session
          console.warn('DEBUG: Using fallback session creation for device authentication');
          const fallbackSessionId = `device-${result.email}-${result.deviceId}-${Date.now()}`;
          localStorage.setItem(SESSION_ID_STORAGE_KEY, fallbackSessionId);
          console.log(`DEBUG: Created fallback session ID: ${fallbackSessionId}`);
          
          // Set the user in state
          dispatch({ type: 'SET_USER', payload: user });
          
          // Capture device information
          await captureDeviceInfo(user.id, 'device-fallback');
          
          // Debug localStorage contents
          debugLocalStorage();
          return;
        }
      }
      
      dispatch({ type: 'SET_ERROR', payload: 'Device authentication failed' });
    } catch (error) {
      console.error('DEBUG: Error in device web crypto sign in:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'An error occurred during device sign in',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signInWithEmailAndPassword = async (email: string, password: string): Promise<User | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Sign in with Firebase
      const userCredential = await signInWithEmailAndPasswordFirebase(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Get the Firebase ID token
      const token = await firebaseUser.getIdToken();
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      
      // Create a session with the backend
      const sessionId = await createSession(token);
      if (sessionId) {
        localStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
      }
      
      // Get the user profile from Firestore
      const userProfile = await userService.getUserProfile(email);
      
      // Check if userProfile exists
      if (!userProfile) {
        console.error('User profile not found for email:', email);
        dispatch({
          type: 'SET_ERROR',
          payload: 'User profile not found',
        });
        return null;
      }
      
      // Store user in localStorage for persistence
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userProfile));
      
      // Set the user in state
      dispatch({ type: 'SET_USER', payload: userProfile });
      
      // Set up token refresh interval
      setupTokenRefreshInterval();
      
      // Capture device information
      await captureDeviceInfo(userProfile.id, 'email_password');
      
      // Debug localStorage contents
      debugLocalStorage();
      
      return userProfile;
    } catch (error) {
      console.error('Error signing in with email and password:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'An error occurred during email/password sign in',
      });
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const sendSmsCode = async (phoneNumber: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const provider = new PhoneAuthProvider(auth);
      const verificationId = await provider.verifyPhoneNumber(phoneNumber, window.recaptchaVerifier);
      // Store the verificationId in window for later use
      window.verificationId = verificationId;
    } catch (error) {
      console.error('DEBUG: Error sending SMS code:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to send SMS code',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const verifySmsCode = async (code: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      if (!window.verificationId) {
        throw new Error('No verification ID found. Please request a new code.');
      }
      const credential = PhoneAuthProvider.credential(window.verificationId, code);
      await signInWithCredential(auth, credential);
    } catch (error) {
      console.error('DEBUG: Error verifying SMS code:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Invalid SMS code',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const verifyTOTP = async (email: string, code: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const userDoc = await getDoc(doc(db, 'users', email));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data() as User;
      const totpSecret = userData.authMethods.totp?.secret;

      if (!totpSecret) {
        throw new Error('TOTP not set up for this user');
      }

      const totp = new OTPAuth.TOTP({
        issuer: "Strategiz",
        label: email,
        algorithm: "SHA1",
        digits: 6,
        period: 30,
        secret: totpSecret
      });

      const isValid = totp.validate({ token: code });
      if (isValid === null) {
        throw new Error('Invalid TOTP code');
      }

      const now = new Date().toISOString();
      await updateDoc(doc(db, 'users', email), {
        'authMethods.totp.lastVerifiedAt': now,
        lastLoginAt: now
      });

      dispatch({ type: 'SET_USER', payload: { ...userData, lastLoginAt: now } });
    } catch (error) {
      console.error('DEBUG: Error verifying TOTP:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Invalid TOTP code',
      });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateUser = async (userId: string, updates: Partial<User>) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, updates);
      
      if (state.user) {
        dispatch({ 
          type: 'SET_USER', 
          payload: { ...state.user, ...updates }
        });
      }
    } catch (error) {
      console.error('DEBUG: Error updating user:', error);
      throw error;
    }
  };

  const getErrorMessage = (error: any): string => {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        signOut,
        signInWithGoogle,
        signInWithFacebook,
        signInWithPasskey,
        signInWithDeviceWebCrypto,
        sendSmsCode,
        verifySmsCode,
        verifyTOTP,
        updateUser,
        signInWithEmailAndPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
